import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { DialogComponent } from 'src/app/components/dialog/dialog.component';
import { ListaDadosAcaoLinha, ListaDadosColuna } from 'src/app/components/lista-dados/lista-dados.component';
import { BeLembrete, BeLembretesListaResult } from 'src/app/models/BeLembrete';
import { DialogData } from 'src/app/models/dialog-data';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { ModoExibicaoPersistidoService } from 'src/app/services/modo-exibicao-persistido.service';
import { LembretesCalendarioDialogComponent, LembretesCalendarioDialogData } from './lembretes-calendario-dialog.component';

@Component({
  selector: 'app-lembretes',
  templateUrl: './lembretes.component.html',
  styleUrls: ['./lembretes.component.css']
})
export class LembretesComponent implements OnInit, OnDestroy {
  public lembretes: BeLembrete[] = [];
  public celularMascarado = '';
  public celularCarregado = false;
  public subscription?: Subscription;
  public exibindoCalendario = true;
  public dadosCalendario?: LembretesCalendarioDialogData;

  private readonly chaveModoExibicao = 'lembretes.modoExibicao';

  public readonly podeAlterar = (row: BeLembrete): boolean =>
    !!row && row.noStatus !== 'Enviado' && row.noStatus !== 'Processando';

  public readonly podeExcluir = (row: BeLembrete): boolean =>
    this.podeAlterar(row) || (!!row && row.noStatus === 'Enviado' && !row.blAtivo);

  public readonly classeLinha = (row: BeLembrete): string =>
    row?.blAtivo ? 'lista-dados-card__row--ativo' : 'lista-dados-card__row--inativo';

  public acoesLinha: ListaDadosAcaoLinha[] = [
    {
      chave: 'ativar',
      texto: 'Ativar',
      icone: 'play_circle',
      visivel: row => !!row && !row.blAtivo && row.noStatus === 'Cancelado'
    },
    {
      chave: 'desativar',
      texto: 'Desativar',
      icone: 'pause_circle',
      visivel: row => !!row?.blAtivo && row.noStatus === 'Agendado'
    },
    {
      chave: 'reenviar',
      texto: 'Reenviar',
      icone: 'send',
      visivel: row => !!row && ['Falha', 'Resultado incerto', 'Expirado'].includes(row.noStatus)
    }
  ];

  public colunasListaDados: ListaDadosColuna[] = [
    {
      campo: 'dtAgendadoData',
      rotulo: 'Data',
      tamanho: '100px',
      editavel: true,
      edicao: { tipoEditor: 'data', validacao: { obrigatorio: true } }
    },
    {
      campo: 'hrAgendado',
      rotulo: 'Horário',
      tamanho: '70px',
      editavel: true,
      edicao: {
        tipoEditor: 'texto',
        validacao: { obrigatorio: true, regex: '^(?:[01]\\d|2[0-3]):[0-5]\\d$' }
      }
    },
    {
      campo: 'dsLembrete',
      rotulo: 'Descrição',
      tamanho: 'calc(100% - 282px)',
      editavel: true,
      edicao: {
        tipoEditor: 'texto',
        validacao: { obrigatorio: true, minLength: 1, maxLength: 500 }
      }
    },
    {
      campo: 'noStatus',
      rotulo: 'Status',
      tamanho: '140px',
      editavel: false
    },
    {
      campo: 'dsUltimoErro',
      rotulo: 'Último erro',
      tamanho: '220px',
      editavel: false
    },
    {
      campo: 'blAtivo',
      rotulo: 'Ativo',
      tamanho: '80px',
      editavel: false,
      tipoExibicao: 'status'
    }
  ].filter(coluna => !['noStatus', 'dsUltimoErro', 'blAtivo'].includes(coluna.campo)) as ListaDadosColuna[];

  public constructor(
    private httpClient: HttpClient,
    private dialog: MatDialog,
    private snackBarService: SnackBarService,
    private modoExibicaoPersistidoService: ModoExibicaoPersistidoService
  ) {}

  public ngOnInit(): void {
    if (this.modoExibicaoPersistidoService.deveRestaurarModoExibicao())
      this.exibindoCalendario = sessionStorage.getItem(this.chaveModoExibicao) !== 'lista';
    else
      sessionStorage.setItem(this.chaveModoExibicao, 'calendario');
    this.prepararDadosCalendario();
    this.carregar();
  }

  public ngOnDestroy(): void {
    this.unsubscribe();
  }

  public onIniciarEdicao(lembrete: BeLembrete): void {
    if (lembrete.id)
      return;

    const padrao = new Date();
    padrao.setMinutes(padrao.getMinutes() + 5, 0, 0);
    lembrete.dtAgendadoData = padrao;
    lembrete.hrAgendado = this.formatarHorario(padrao);
    lembrete.dsLembrete = '';
    lembrete.noStatus = 'Agendado';
    lembrete.blAtivo = true;
  }

  public onSalvarLinha(lembrete: BeLembrete): void {
    const dtAgendado = this.montarDataHora(lembrete.dtAgendadoData, lembrete.hrAgendado);
    if (!dtAgendado) {
      this.snackBarService.exibirErro('Informe uma data e um horário válidos.');
      return;
    }

    if (new Date(dtAgendado).getTime() <= Date.now()) {
      this.snackBarService.exibirErro('A data e o horário do lembrete devem estar no futuro.');
      return;
    }

    const payload: BeLembrete = {
      id: lembrete.id ?? null,
      dtAgendado,
      dsLembrete: lembrete.dsLembrete.trim(),
      noStatus: lembrete.noStatus,
      blAtivo: lembrete.blAtivo
    };

    this.unsubscribe();
    const requisicao = payload.id
      ? this.httpClient.put<BeLembrete>('/api/Lembretes', payload)
      : this.httpClient.post<BeLembrete>('/api/Lembretes', payload);

    this.subscription = requisicao.subscribe({
      next: res => {
        Object.assign(lembrete, this.prepararParaTela(res));
        this.ordenar();
        this.unsubscribe();
        this.snackBarService.exibirSucesso(payload.id ? 'Lembrete salvo!' : 'Lembrete criado!');
      },
      error: err => {
        this.unsubscribe();
        this.snackBarService.exibirErro(this.obterMensagemErro(err, 'Não foi possível salvar o lembrete.'));
      }
    });
  }

  public executarAcaoLinha(evento: { chave: string; linha: BeLembrete }, aoConcluir?: () => void): void {
    if (evento.chave === 'ativar')
      this.confirmarAlteracaoAtivo(evento.linha, true, aoConcluir);
    else if (evento.chave === 'desativar')
      this.confirmarAlteracaoAtivo(evento.linha, false, aoConcluir);
    else if (evento.chave === 'reenviar')
      this.confirmarReenvio(evento.linha);
  }

  public abrirCalendario(): void {
    this.prepararDadosCalendario();
    this.exibindoCalendario = true;
    sessionStorage.setItem(this.chaveModoExibicao, 'calendario');
  }

  public voltarParaLista(): void {
    this.exibindoCalendario = false;
    sessionStorage.setItem(this.chaveModoExibicao, 'lista');
  }

  private prepararDadosCalendario(): void {
    this.dadosCalendario = {
      lembretes: this.lembretes,
      podeAlterar: this.podeAlterar,
      podeExcluir: this.podeExcluir,
      onSalvar: (lembrete: BeLembrete, aoSalvar: (lembreteSalvo: BeLembrete) => void) =>
        this.salvarLembreteCalendario(lembrete, aoSalvar),
      onAcao: (evento: { chave: string; linha: BeLembrete }, aoConcluir?: () => void) =>
        this.executarAcaoLinha(evento, aoConcluir),
      onExcluir: (lembrete: BeLembrete, aoExcluir: () => void) => this.onExcluir(lembrete, aoExcluir)
    };
  }

  public onExcluir(lembrete: BeLembrete, aoExcluir?: () => void): void {
    if (!lembrete.id || this.subscription || !this.podeExcluir(lembrete))
      return;

    this.confirmar(
      'Alerta',
      'Deseja excluir esse registro?',
      () => {
        this.subscription = this.httpClient.delete(`/api/Lembretes/${lembrete.id}`).subscribe({
          next: () => {
            const indice = this.lembretes.findIndex(item => item.id === lembrete.id);
            if (indice >= 0)
              this.lembretes.splice(indice, 1);
            this.lembretes = [...this.lembretes];
            aoExcluir?.();
            this.unsubscribe();
            this.snackBarService.exibirSucesso('Lembrete excluído!');
          },
          error: err => this.tratarErro(err, 'Não foi possível excluir o lembrete.')
        });
      });
  }

  public salvarLembreteCalendario(lembrete: BeLembrete, aoSalvar: (lembreteSalvo: BeLembrete) => void): void {
    const dtAgendado = this.montarDataHora(lembrete.dtAgendadoData, lembrete.hrAgendado);
    if (!dtAgendado) {
      this.snackBarService.exibirErro('Informe uma data e um horário válidos.');
      return;
    }

    if (new Date(dtAgendado).getTime() <= Date.now()) {
      this.snackBarService.exibirErro('A data e o horário do lembrete devem estar no futuro.');
      return;
    }

    const payload: BeLembrete = {
      id: lembrete.id ?? null,
      dtAgendado,
      dsLembrete: lembrete.dsLembrete.trim(),
      noStatus: lembrete.noStatus,
      blAtivo: lembrete.blAtivo
    };

    this.unsubscribe();
    const requisicao = payload.id
      ? this.httpClient.put<BeLembrete>('/api/Lembretes', payload)
      : this.httpClient.post<BeLembrete>('/api/Lembretes', payload);

    this.subscription = requisicao.subscribe({
      next: res => {
        const lembreteSalvo = this.prepararParaTela(res);
        const indice = this.lembretes.findIndex(item => item.id === lembreteSalvo.id);
        if (indice >= 0)
          Object.assign(this.lembretes[indice], lembreteSalvo);
        else
          this.lembretes.push(lembreteSalvo);
        this.ordenar();
        aoSalvar(lembreteSalvo);
        this.unsubscribe();
        this.snackBarService.exibirSucesso(payload.id ? 'Lembrete salvo!' : 'Lembrete criado!');
      },
      error: err => this.tratarErro(err, 'Não foi possível salvar o lembrete.')
    });
  }

  private confirmarAlteracaoAtivo(lembrete: BeLembrete, ativo: boolean, aoConcluir?: () => void): void {
    if (!lembrete.id || this.subscription)
      return;

    const verbo = ativo ? 'ativar' : 'desativar';
    this.confirmar(
      `${ativo ? 'Ativar' : 'Desativar'} lembrete`,
      `Deseja ${verbo} o lembrete "${lembrete.dsLembrete}"?`,
      () => {
        this.subscription = this.httpClient
          .put<BeLembrete>(`/api/Lembretes/${lembrete.id}/ativo/${ativo}`, null)
          .subscribe({
            next: res => {
              Object.assign(lembrete, this.prepararParaTela(res));
              this.lembretes = [...this.lembretes];
              this.unsubscribe();
              this.snackBarService.exibirSucesso(`Lembrete ${ativo ? 'ativado' : 'desativado'}!`);
              aoConcluir?.();
            },
            error: err => this.tratarErro(err, `Não foi possível ${verbo} o lembrete.`)
          });
      });
  }

  private confirmarReenvio(lembrete: BeLembrete): void {
    if (!lembrete.id || this.subscription)
      return;

    this.confirmar(
      'Reenviar lembrete',
      'O lembrete será colocado na fila para uma nova tentativa. Deseja continuar?',
      () => {
        this.subscription = this.httpClient
          .put<BeLembrete>(`/api/Lembretes/${lembrete.id}/reenviar`, null)
          .subscribe({
            next: res => {
              Object.assign(lembrete, this.prepararParaTela(res));
              this.ordenar();
              this.unsubscribe();
              this.snackBarService.exibirSucesso('Lembrete colocado na fila de envio!');
            },
            error: err => this.tratarErro(err, 'Não foi possível reenviar o lembrete.')
          });
      });
  }

  private carregar(): void {
    this.unsubscribe();
    this.subscription = this.httpClient.get<BeLembretesListaResult>('/api/Lembretes').subscribe({
      next: res => {
        this.celularMascarado = res.nrCelularMascarado;
        this.celularCarregado = true;
        this.lembretes = (res.lembretes ?? []).map(item => this.prepararParaTela(item));
        this.prepararDadosCalendario();
        this.unsubscribe();
      },
      error: err => this.tratarErro(err, 'Não foi possível carregar os lembretes.')
    });
  }

  private prepararParaTela(item: BeLembrete): BeLembrete {
    const partes = (item.dtAgendado ?? '').split('T');
    return {
      ...item,
      dtAgendadoData: partes[0] ? new Date(`${partes[0]}T00:00:00`) : null,
      hrAgendado: partes[1]?.substring(0, 5) ?? ''
    };
  }

  private montarDataHora(data?: Date | { toDate: () => Date } | null, horario?: string): string | null {
    const dataLocal = data instanceof Date
      ? data
      : data && typeof data.toDate === 'function'
        ? data.toDate()
        : null;

    if (!dataLocal || Number.isNaN(dataLocal.getTime()) || !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(horario ?? ''))
      return null;

    const ano = dataLocal.getFullYear();
    const mes = String(dataLocal.getMonth() + 1).padStart(2, '0');
    const dia = String(dataLocal.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}T${horario}:00`;
  }

  private formatarHorario(data: Date): string {
    return `${String(data.getHours()).padStart(2, '0')}:${String(data.getMinutes()).padStart(2, '0')}`;
  }

  private ordenar(): void {
    this.lembretes = [...this.lembretes].sort((a, b) => b.dtAgendado.localeCompare(a.dtAgendado));
  }

  private confirmar(titulo: string, mensagem: string, executar: () => void): void {
    const data: DialogData = {
      title: titulo,
      message: mensagem,
      buttonText: 'Não',
      secondButtonText: 'Sim'
    };
    this.dialog.open(DialogComponent, { data }).afterClosed().subscribe(result => {
      if (result) {
        this.unsubscribe();
        executar();
      }
    });
  }

  private tratarErro(erro: any, mensagemPadrao: string): void {
    this.unsubscribe();
    this.snackBarService.exibirErro(this.obterMensagemErro(erro, mensagemPadrao));
  }

  private obterMensagemErro(erro: any, mensagemPadrao: string): string {
    if (typeof erro?.error === 'string' && erro.error.trim())
      return erro.error;
    return erro?.error?.message ?? erro?.error?.mensagem ?? mensagemPadrao;
  }

  private unsubscribe(): void {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
  }
}
